import React from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  makeStyles,
  Typography,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from "@material-ui/core";
import { Cancel, FileCopyOutlined } from "@material-ui/icons";

import {
  removeCreative,
  duplicateCreative
} from "../../../actions/app/creatives";
import { getAssetTypeInfo, resolveExistingCreativePreview } from "./fileTypeUtils";

const content = {
  paddingBottom: 0,
  padding: 12,
  textAlign: "left"
};

const useStyles = makeStyles((theme) => ({
  card: {
    width: 260,
    margin: 10,
    position: "relative",
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    paddingTop: theme.spacing(6)
  },
  previewArea: {
    height: 150,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden"
  },
  mediaImg: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain"
  },
  icon: {
    position: "absolute",
    right: 0,
    top: 0
  },
  duplicateIcon: {
    position: "absolute",
    right: theme.spacing(6),
    top: 0
  },
  name: {
    wordBreak: "break-all"
  },
  typography: {
    wordBreak: "break-all",
    paddingBottom: theme.spacing(1)
  },
  iconPlaceholder: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.text.secondary
  }
}));

const AssetPreview = ({ asset, isThumbnailEnabled, uploadLimit = 20 }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const creativeCount = useSelector((state) => state.creatives.allIds.length);

  // Duplicating only makes sense for an uploaded file. An existing GCM pick
  // already lives in DCM and goes through the existing-asset path in
  // insertCreatives, where a copy would mean a second creative on the same
  // asset. Steps that only ever take one file (uploadLimit 1) hide it too.
  const canDuplicate = asset.dcmCreative === undefined && uploadLimit > 1;
  const atLimit = creativeCount >= uploadLimit;

  const updateDimensions = () => {
    if (asset.dcmCreative !== undefined) {
      return `${asset.dcmCreative.size.width} x ${asset.dcmCreative.size.height}`;
    } else if (asset.dimensions === "N/A") {
      if (asset.file.type !== undefined &&
        (asset.file.type.includes("video") || asset.file.type.includes("audio"))) {
        return "0 x 0";
      }
      return asset.dimensions;
    } else {
      return asset.dimensions;
    }
  };

  return (
    <Card className={classes.card}>
      {canDuplicate ? (
        <Tooltip
          title={
            atLimit
              ? `Upload limit is ${uploadLimit} files`
              : "Duplicate this creative"
          }
          placement="top"
        >
          <span className={classes.duplicateIcon}>
            <IconButton
              aria-label="Duplicate creative"
              disabled={atLimit}
              onClick={() => dispatch(duplicateCreative(asset.id))}
            >
              <FileCopyOutlined color={atLimit ? "disabled" : "action"} />
            </IconButton>
          </span>
        </Tooltip>
      ) : null}
      <IconButton
        className={classes.icon}
        onClick={() => dispatch(removeCreative(asset.id))}
      >
        <Cancel filled="true" color="action" />
      </IconButton>
      <div className={classes.previewArea}>
        {(() => {
          const { icon: FileIcon } = getAssetTypeInfo(asset);
          if (asset.file && asset.file.type && asset.file.type.startsWith('audio/') && asset.preview) {
            return (
              <audio controls style={{ width: '100%' }}>
                <source src={asset.preview} type={asset.file.type} />
              </audio>
            );
          }
          const existingPreview = resolveExistingCreativePreview(asset);
          if (existingPreview && existingPreview.previewType === 'audio') {
            return (
              <audio controls style={{ width: '100%' }}>
                <source src={existingPreview.url} />
              </audio>
            );
          }
          if (isThumbnailEnabled && existingPreview && existingPreview.previewType === 'image') {
            return (
              <img
                src={existingPreview.url}
                alt={asset.taxonomy.name || asset.dcmCreative.name}
                className={classes.mediaImg}
              />
            );
          }
          if (FileIcon) {
            return (
              <div className={classes.iconPlaceholder}>
                <FileIcon style={{ fontSize: 80 }} />
              </div>
            );
          }
          if (isThumbnailEnabled && asset.preview) {
            return (
              <img
                src={asset.preview}
                alt={asset.file.name}
                className={classes.mediaImg}
              />
            );
          }
          return null;
        })()}
      </div>
      <CardContent style={content}>
        {asset.taxonomy.name ? (
          <Typography
            variant="body2"
            color="textSecondary"
            component="p"
            className={classes.typography}
          >
            Creative name: {asset.taxonomy.name}
          </Typography>
        ) : null}
        <Typography
          variant="body2"
          color="textSecondary"
          component="p"
          className={classes.typography}
        >
          File name: {asset.file.name ? asset.file.name : asset.name}
        </Typography>
        <Typography variant="body2" color="textSecondary" component="p">
          Dimensions: {updateDimensions()}{asset.isRetina ? " (retina 2x)" : ""}
        </Typography>
        <Typography variant="body2" color="textSecondary" component="p">
          Type: {getAssetTypeInfo(asset).label}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default AssetPreview;
